import React, { Children } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
export function PrivacyPolicyPage() {
  return <>
      <Helmet data-id="element-9718">
        <title data-id="element-9719">Privacy Policy | ToxicDetector</title>
        <meta name="description" content="Learn how ToxicDetector protects your data and privacy when analyzing messages for toxicity and manipulation." data-id="element-9720" />
        <link rel="canonical" href="https://toxicdetector.com/privacy" data-id="element-9721" />
        <meta property="og:title" content="Privacy Policy | ToxicDetector" data-id="element-9722" />
        <meta property="og:description" content="Learn how ToxicDetector protects your data and privacy when analyzing messages for toxicity and manipulation." data-id="element-9723" />
        <meta property="og:url" content="https://toxicdetector.com/privacy" data-id="element-9724" />
        <meta property="og:type" content="website" data-id="element-9725" />
        <meta name="robots" content="index, follow" data-id="element-9726" />
      </Helmet>
      <div className="flex flex-col w-full min-h-screen bg-[#f8f6f2]" data-id="element-9727">
        <header className="bg-white border-b border-gray-200" data-id="element-9728">
          <div className="max-w-4xl mx-auto px-5 py-4" data-id="element-9729">
            <Link to="/" className="inline-flex items-center text-gray-600 hover:text-purple-600 text-sm mb-2" data-id="element-9730">
              <ArrowLeft size={16} className="mr-1" data-id="element-9731" />
              Back to Home
            </Link>
            <h1 className="text-2xl font-bold" data-id="element-9732">Privacy Policy</h1>
            <p className="text-gray-600 text-sm mt-1" data-id="element-9733">
              Last updated:{' '}
              {new Date().toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric'
            })}
            </p>
          </div>
        </header>

        <div className="max-w-4xl mx-auto w-full px-5 py-8" data-id="element-9734">
          <div className="bg-white rounded-xl p-8 shadow-sm" data-id="element-9735">
            <section className="mb-8" data-id="element-9736">
              <h2 className="text-xl font-bold mb-4" data-id="element-9737">Introduction</h2>
              <p className="text-gray-700 mb-4" data-id="element-9738">
                Welcome to ToxicDetector ("we," "our," or "us"). We are
                committed to protecting your privacy and ensuring the security
                of your personal information. This Privacy Policy explains how
                we collect, use, disclose, and safeguard your information when
                you use our web application and services.
              </p>
              <p className="text-gray-700" data-id="element-9739">
                By using ToxicDetector, you agree to the collection and use of
                information in accordance with this Privacy Policy. If you do
                not agree with our policies and practices, please do not use our
                services.
              </p>
            </section>

            <section className="mb-8" data-id="element-9740">
              <h2 className="text-xl font-bold mb-4" data-id="element-9741">
                1. Information We Collect
              </h2>

              <h3 className="text-lg font-semibold mb-3" data-id="element-9742">
                1.1 Information You Provide
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-9743">
                We collect information that you voluntarily provide when using
                our services:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2" data-id="element-9744">
                <li data-id="element-9745">
                  <strong data-id="element-9746">Account Information:</strong> Name, email address,
                  password (encrypted), and profile details
                </li>
                <li data-id="element-9747">
                  <strong data-id="element-9748">Uploaded Content:</strong> Screenshots and images of
                  text conversations you upload for analysis
                </li>
                <li data-id="element-9749">
                  <strong data-id="element-9750">Analysis Data:</strong> Results from toxicity
                  analysis, including detected patterns and risk scores
                </li>
                <li data-id="element-9751">
                  <strong data-id="element-9752">Communication Preferences:</strong> Settings for
                  notifications, marketing emails, and data sharing preferences
                </li>
              </ul>

              <h3 className="text-lg font-semibold mb-3" data-id="element-9753">
                1.2 Automatically Collected Information
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-9754">
                When you access our services, we automatically collect certain
                information:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2" data-id="element-9755">
                <li data-id="element-9756">
                  <strong data-id="element-9757">Usage Data:</strong> Pages visited, features used,
                  time spent on the platform, and interaction patterns
                </li>
                <li data-id="element-9758">
                  <strong data-id="element-9759">Device Information:</strong> Browser type, operating
                  system, device identifiers, and IP address
                </li>
                <li data-id="element-9760">
                  <strong data-id="element-9761">Authentication Data:</strong> Login timestamps,
                  session information, and security events
                </li>
                <li data-id="element-9762">
                  <strong data-id="element-9763">Analytics Data:</strong> Aggregated usage statistics
                  and performance metrics
                </li>
              </ul>

              <h3 className="text-lg font-semibold mb-3" data-id="element-9764">
                1.3 Cookies and Tracking Technologies
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-9765">
                We use cookies and similar tracking technologies to enhance your
                experience, maintain session security, and analyze usage
                patterns. You can control cookie preferences through your
                browser settings, though some features may not function properly
                if cookies are disabled.
              </p>
            </section>

            <section className="mb-8" data-id="element-9766">
              <h2 className="text-xl font-bold mb-4" data-id="element-9767">
                2. How We Use Your Information
              </h2>
              <p className="text-gray-700 mb-4" data-id="element-9768">
                We use the collected information for the following purposes:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2" data-id="element-9769">
                <li data-id="element-9770">
                  <strong data-id="element-9771">Service Delivery:</strong> To provide, maintain, and
                  improve our toxicity detection and analysis services
                </li>
                <li data-id="element-9772">
                  <strong data-id="element-9773">AI Analysis:</strong> To process uploaded screenshots
                  using artificial intelligence to detect toxic patterns in
                  conversations
                </li>
                <li data-id="element-9774">
                  <strong data-id="element-9775">Account Management:</strong> To create and manage your
                  account, authenticate your identity, and provide customer
                  support
                </li>
                <li data-id="element-9776">
                  <strong data-id="element-9777">Communication:</strong> To send you service updates,
                  security alerts, and (with your consent) marketing
                  communications
                </li>
                <li data-id="element-9778">
                  <strong data-id="element-9779">Security:</strong> To detect, prevent, and address
                  technical issues, fraud, and security vulnerabilities
                </li>
                <li data-id="element-9780">
                  <strong data-id="element-9781">Analytics:</strong> To understand usage patterns,
                  improve our algorithms, and enhance user experience
                </li>
                <li data-id="element-9782">
                  <strong data-id="element-9783">Legal Compliance:</strong> To comply with legal
                  obligations and enforce our Terms of Service
                </li>
              </ul>
            </section>

            <section className="mb-8" data-id="element-9784">
              <h2 className="text-xl font-bold mb-4" data-id="element-9785">
                3. How We Share Your Information
              </h2>

              <h3 className="text-lg font-semibold mb-3" data-id="element-9786">
                3.1 Third-Party Service Providers
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-9787">
                We share information with trusted third-party service providers
                who assist in operating our platform:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2" data-id="element-9788">
                <li data-id="element-9789">
                  <strong data-id="element-9790">Supabase:</strong> Cloud infrastructure for
                  authentication, database, and file storage
                </li>
                <li data-id="element-9791">
                  <strong data-id="element-9792">OpenAI:</strong> AI processing for toxicity analysis
                  (text content only, not personal identifiers)
                </li>
                <li data-id="element-9793">
                  <strong data-id="element-9794">Analytics Providers:</strong> For usage analytics and
                  performance monitoring (when enabled in your settings)
                </li>
              </ul>
              <p className="text-gray-700 mb-4" data-id="element-9795">
                These providers are contractually obligated to protect your
                information and use it only for the specific services they
                provide to us.
              </p>

              <h3 className="text-lg font-semibold mb-3" data-id="element-9796">3.2 Public Sharing</h3>
              <p className="text-gray-700 mb-4" data-id="element-9797">
                When you choose to share analysis results using our sharing
                feature, a public link is generated that allows anyone with the
                link to view the analysis results. Shared results do not include
                your personal information but do include the analysis data and
                patterns detected.
              </p>

              <h3 className="text-lg font-semibold mb-3" data-id="element-9798">
                3.3 Legal Requirements
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-9799">
                We may disclose your information if required by law, court
                order, or governmental regulation, or if we believe disclosure
                is necessary to protect our rights, your safety, or the safety
                of others.
              </p>

              <h3 className="text-lg font-semibold mb-3" data-id="element-9800">
                3.4 Business Transfers
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-9801">
                In the event of a merger, acquisition, or sale of assets, your
                information may be transferred to the acquiring entity. We will
                notify you of any such change and provide options regarding your
                information.
              </p>
            </section>

            <section className="mb-8" data-id="element-9802">
              <h2 className="text-xl font-bold mb-4" data-id="element-9803">
                4. Data Retention and Storage
              </h2>
              <p className="text-gray-700 mb-4" data-id="element-9804">
                We retain your information for as long as necessary to provide
                our services and comply with legal obligations:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2" data-id="element-9805">
                <li data-id="element-9806">
                  <strong data-id="element-9807">Account Data:</strong> Retained while your account is
                  active and for 90 days after account deletion
                </li>
                <li data-id="element-9808">
                  <strong data-id="element-9809">Uploaded Screenshots:</strong> Stored according to
                  your retention settings (30, 90, 180 days, or indefinitely)
                </li>
                <li data-id="element-9810">
                  <strong data-id="element-9811">Analysis Results:</strong> Retained according to your
                  chosen retention period
                </li>
                <li data-id="element-9812">
                  <strong data-id="element-9813">Usage Logs:</strong> Retained for up to 12 months for
                  security and analytics purposes
                </li>
              </ul>
              <p className="text-gray-700 mb-4" data-id="element-9814">
                You can manage your data retention preferences in the Settings
                section under Privacy Settings. You can also request deletion of
                specific scans or your entire account at any time.
              </p>
            </section>

            <section className="mb-8" data-id="element-9815">
              <h2 className="text-xl font-bold mb-4" data-id="element-9816">5. Data Security</h2>
              <p className="text-gray-700 mb-4" data-id="element-9817">
                We implement industry-standard security measures to protect your
                information:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2" data-id="element-9818">
                <li data-id="element-9819">
                  <strong data-id="element-9820">Encryption:</strong> All data is encrypted in transit
                  (TLS/SSL) and at rest
                </li>
                <li data-id="element-9821">
                  <strong data-id="element-9822">Authentication:</strong> Secure password hashing and
                  optional two-factor authentication (2FA)
                </li>
                <li data-id="element-9823">
                  <strong data-id="element-9824">Access Controls:</strong> Strict access controls and
                  authentication for all data access
                </li>
                <li data-id="element-9825">
                  <strong data-id="element-9826">Regular Audits:</strong> Periodic security assessments
                  and vulnerability testing
                </li>
                <li data-id="element-9827">
                  <strong data-id="element-9828">Secure Infrastructure:</strong> Hosted on secure, SOC
                  2 compliant cloud infrastructure
                </li>
              </ul>
              <p className="text-gray-700 mb-4" data-id="element-9829">
                While we strive to protect your information, no method of
                transmission or storage is 100% secure. We cannot guarantee
                absolute security but continuously work to improve our security
                measures.
              </p>
            </section>

            <section className="mb-8" data-id="element-9830">
              <h2 className="text-xl font-bold mb-4" data-id="element-9831">6. Your Privacy Rights</h2>
              <p className="text-gray-700 mb-4" data-id="element-9832">
                Depending on your location, you may have the following rights
                regarding your personal information:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2" data-id="element-9833">
                <li data-id="element-9834">
                  <strong data-id="element-9835">Access:</strong> Request a copy of the personal
                  information we hold about you
                </li>
                <li data-id="element-9836">
                  <strong data-id="element-9837">Correction:</strong> Update or correct inaccurate
                  personal information
                </li>
                <li data-id="element-9838">
                  <strong data-id="element-9839">Deletion:</strong> Request deletion of your personal
                  information (subject to legal retention requirements)
                </li>
                <li data-id="element-9840">
                  <strong data-id="element-9841">Data Portability:</strong> Receive your data in a
                  structured, machine-readable format
                </li>
                <li data-id="element-9842">
                  <strong data-id="element-9843">Opt-Out:</strong> Opt out of marketing communications
                  and certain data processing activities
                </li>
                <li data-id="element-9844">
                  <strong data-id="element-9845">Restriction:</strong> Request restriction of
                  processing in certain circumstances
                </li>
                <li data-id="element-9846">
                  <strong data-id="element-9847">Objection:</strong> Object to processing based on
                  legitimate interests
                </li>
              </ul>
              <p className="text-gray-700 mb-4" data-id="element-9848">
                To exercise these rights, visit your Settings page or contact us
                at toxicdetector25@gmail.com. We will respond to your request
                within 30 days.
              </p>
            </section>

            <section className="mb-8" data-id="element-9849">
              <h2 className="text-xl font-bold mb-4" data-id="element-9850">
                7. International Data Transfers
              </h2>
              <p className="text-gray-700 mb-4" data-id="element-9851">
                Your information may be transferred to and processed in
                countries other than your country of residence. These countries
                may have different data protection laws. When we transfer your
                information internationally, we ensure appropriate safeguards
                are in place to protect your information in accordance with this
                Privacy Policy.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-9852">
                For users in the European Economic Area (EEA), we comply with
                GDPR requirements for international data transfers, including
                the use of Standard Contractual Clauses where applicable.
              </p>
            </section>

            <section className="mb-8" data-id="element-9853">
              <h2 className="text-xl font-bold mb-4" data-id="element-9854">8. Children's Privacy</h2>
              <p className="text-gray-700 mb-4" data-id="element-9855">
                ToxicDetector is not intended for use by individuals under the
                age of 18. We do not knowingly collect personal information from
                children under 18. If we become aware that we have collected
                personal information from a child under 18, we will take steps
                to delete such information promptly.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-9856">
                If you are a parent or guardian and believe your child has
                provided us with personal information, please contact us at
                toxicdetector25@gmail.com.
              </p>
            </section>

            <section className="mb-8" data-id="element-9857">
              <h2 className="text-xl font-bold mb-4" data-id="element-9858">
                9. California Privacy Rights
              </h2>
              <p className="text-gray-700 mb-4" data-id="element-9859">
                If you are a California resident, you have additional rights
                under the California Consumer Privacy Act (CCPA):
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2" data-id="element-9860">
                <li data-id="element-9861">
                  Right to know what personal information is collected, used,
                  shared, or sold
                </li>
                <li data-id="element-9862">Right to delete personal information held by businesses</li>
                <li data-id="element-9863">
                  Right to opt-out of the sale of personal information (we do
                  not sell personal information)
                </li>
                <li data-id="element-9864">Right to non-discrimination for exercising CCPA rights</li>
              </ul>
              <p className="text-gray-700 mb-4" data-id="element-9865">
                To exercise these rights, contact us at
                toxicdetector25@gmail.com or use the privacy controls in your
                Settings.
              </p>
            </section>

            <section className="mb-8" data-id="element-9866">
              <h2 className="text-xl font-bold mb-4" data-id="element-9867">
                10. European Privacy Rights (GDPR)
              </h2>
              <p className="text-gray-700 mb-4" data-id="element-9868">
                If you are located in the European Economic Area (EEA), you have
                rights under the General Data Protection Regulation (GDPR):
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2" data-id="element-9869">
                <li data-id="element-9870">
                  Legal basis for processing: Consent, contract performance,
                  legitimate interests, and legal obligations
                </li>
                <li data-id="element-9871">Right to withdraw consent at any time</li>
                <li data-id="element-9872">
                  Right to lodge a complaint with your local data protection
                  authority
                </li>
                <li data-id="element-9873">
                  Right to data portability in a structured, commonly used
                  format
                </li>
              </ul>
              <p className="text-gray-700 mb-4" data-id="element-9874">
                Our data protection officer can be reached at
                toxicdetector25@gmail.com for GDPR-related inquiries.
              </p>
            </section>

            <section className="mb-8" data-id="element-9875">
              <h2 className="text-xl font-bold mb-4" data-id="element-9876">
                11. Changes to This Privacy Policy
              </h2>
              <p className="text-gray-700 mb-4" data-id="element-9877">
                We may update this Privacy Policy from time to time to reflect
                changes in our practices, technology, legal requirements, or
                other factors. We will notify you of any material changes by:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2" data-id="element-9878">
                <li data-id="element-9879">
                  Posting the updated Privacy Policy on this page with a new
                  "Last Updated" date
                </li>
                <li data-id="element-9880">
                  Sending you an email notification (if you have opted in to
                  receive such notifications)
                </li>
                <li data-id="element-9881">Displaying a prominent notice on our platform</li>
              </ul>
              <p className="text-gray-700 mb-4" data-id="element-9882">
                Your continued use of ToxicDetector after any changes indicates
                your acceptance of the updated Privacy Policy.
              </p>
            </section>

            <section className="mb-8" data-id="element-9883">
              <h2 className="text-xl font-bold mb-4" data-id="element-9884">12. Contact Us</h2>
              <p className="text-gray-700 mb-4" data-id="element-9885">
                If you have questions, concerns, or requests regarding this
                Privacy Policy or our privacy practices, please contact us:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-9886">
                <p className="text-gray-700 mb-2" data-id="element-9887">
                  <strong data-id="element-9888">Email:</strong> toxicdetector25@gmail.com
                </p>
                <p className="text-gray-700 mb-2" data-id="element-9889">
                  <strong data-id="element-9890">Support:</strong> support@toxicdetector.com
                </p>
                <p className="text-gray-700" data-id="element-9891">
                  <strong data-id="element-9892">Data Protection Officer:</strong>{' '}
                  toxicdetector25@gmail.com
                </p>
              </div>
            </section>

            <section className="mb-8" data-id="element-9893">
              <h2 className="text-xl font-bold mb-4" data-id="element-9894">13. Consent</h2>
              <p className="text-gray-700 mb-4" data-id="element-9895">
                By using ToxicDetector, you consent to our Privacy Policy and
                agree to its terms. If you do not agree with this Privacy
                Policy, please discontinue use of our services immediately.
              </p>
            </section>

            <div className="border-t border-gray-200 pt-6 mt-8" data-id="element-9896">
              <p className="text-gray-600 text-sm text-center" data-id="element-9897">
                © {new Date().getFullYear()} ToxicDetector. All rights
                reserved.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>;
}